import * as React from 'react';

import { ChevronIcon } from '../../../../../assets';
import { HorizontalCarouselContainer, LeftScrollButton, RightScrollButton } from './HorizontalCarousel.styles';

const SCROLL_CLICK_STEP_SIZE_IN_PX = 5;
const PRESS_AND_HOLD_INTERVAL = 10;

enum ScrollDirection {
  LEFT,
  RIGHT,
}

interface HorizontalCarouselProps {
  children: React.ReactNode;
  /* Valid CSS `string` to be use as gradient for CSS rule `background`;*/
  gradient?: string;
}

const HorizontalCarousel = ({ children, gradient }: HorizontalCarouselProps) => {
  // State
  const [showOverlayLeft, setShowOverlayLeft] = React.useState(false);
  const [showOverlayRight, setShowOverlayRight] = React.useState(true);
  const scrollableContainerRef = React.useRef<HTMLDivElement>(null);
  const intervalRef = React.useRef(null);

  // Effects
  React.useLayoutEffect(() => {
    if (children) {
      setOverlayStates(scrollableContainerRef.current);
    }
  }, [children]);

  // Handlers
  const handleScroll = ({ currentTarget }: React.UIEvent<HTMLDivElement>) => {
    setOverlayStates(currentTarget);
  };

  const handlePressStart = (scrollDirection: ScrollDirection) => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      switch (scrollDirection) {
        case ScrollDirection.LEFT:
          handleScrollLeftClick();
          break;
        case ScrollDirection.RIGHT:
          handleScrollRightClick();
          break;

        default:
          break;
      }
    }, PRESS_AND_HOLD_INTERVAL);
  };

  const handlePressEnd = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Helpers
  const handleScrollLeftClick = () => {
    (scrollableContainerRef.current as any as HTMLDivElement).scrollLeft -= SCROLL_CLICK_STEP_SIZE_IN_PX;
  };

  const handleScrollRightClick = () => {
    (scrollableContainerRef.current as any as HTMLDivElement).scrollLeft += SCROLL_CLICK_STEP_SIZE_IN_PX;
  };

  const setOverlayStates = (currentTarget: HTMLDivElement) => {
    const { scrollLeft, scrollWidth, offsetWidth } = currentTarget;
    const scrollEnd = scrollWidth - offsetWidth;
    const nextShowOverlayLeft = scrollLeft > 0;
    // We add a small offset to the scrollEnd to avoid the right overlay showing when the scroll is at the end.
    const nextShowOverlayRight = scrollLeft < scrollEnd - 2;

    setShowOverlayLeft(nextShowOverlayLeft);
    setShowOverlayRight(nextShowOverlayRight);
  };

  return (
    <HorizontalCarouselContainer
      $isLeftOverlayVisible={showOverlayLeft}
      $isRightOverlayVisible={showOverlayRight}
      $gradient={gradient}
      onScroll={handleScroll}
      ref={scrollableContainerRef}
    >
      <LeftScrollButton
        $isVisible={showOverlayLeft}
        icon={<ChevronIcon />}
        onPressStart={() => handlePressStart(ScrollDirection.LEFT)}
        onPressEnd={handlePressEnd}
        onMouseLeave={handlePressEnd}
      />
      {children}
      <RightScrollButton
        $isVisible={showOverlayRight}
        icon={<ChevronIcon />}
        onPressStart={() => handlePressStart(ScrollDirection.RIGHT)}
        onPressEnd={handlePressEnd}
        onMouseLeave={handlePressEnd}
      />
    </HorizontalCarouselContainer>
  );
};

export default HorizontalCarousel;
